import type { Question } from '../types';
import { 
  INSIGHT_TOPICS, 
  HAZARD_TOPICS, 
  KNOWLEDGE_TOPICS,
  ARCHIVE_TOPICS 
} from './categoryStructure';

// Import all question files (CBR 2026 consolidated)
import { alcoholDrugsQuestions } from './alcoholDrugsQuestions';
import { constructionZonesQuestions } from './constructionZonesQuestions';
import { emergencyProceduresQuestions } from './emergencyProceduresQuestions';
import { environmentalZonesQuestions } from './environmentalZonesQuestions';
import { fatigueRestQuestions } from './fatigueRestQuestions';
import { incidentFirstAidQuestions } from './incidentFirstAidQuestions';
import { mandatorySignsQuestions } from './mandatorySignsQuestions';
import { motorwayRulesQuestions } from './motorwayRulesQuestions';
import { nightVisibilityQuestions } from './nightVisibilityQuestions';
import { pedestrianCrossingsQuestions } from './pedestrianCrossingsQuestions';
import { priorityRulesQuestions } from './priorityRulesQuestions';
import { realExamQuestions } from './realExamQuestions';
import { riskAwarenessQuestions } from './riskAwarenessQuestions';
import { roadMarkingsQuestions } from './roadMarkingsQuestions';
import { signIdentificationQuestions } from './signIdentificationQuestions';
import { technologySafetyQuestions } from './technologySafetyQuestions';
import { tramInteractionsQuestions } from './tramInteractionsQuestions';
import { vehicleCategoriesQuestions } from './vehicleCategoriesQuestions';
import { vehicleDocumentationQuestions } from './vehicleDocumentationQuestions';
import { vehicleHandlingQuestions } from './vehicleHandlingQuestions';
import { prohibitorySignsQuestions } from './prohibitorySignsQuestions';
import { weatherConditionsQuestions } from './weatherConditionsQuestions';
import { adasSafetyQuestions } from './adasSafetyQuestions';
import { trafficLightsSignalsQuestions } from './trafficLightsSignalsQuestions';
import { bicycleInteractionsQuestions } from './bicycleInteractionsQuestions';
import { roundaboutRulesQuestions } from './roundaboutRulesQuestions';
import { specializedDrivingQuestions } from './specializedDrivingQuestions';
import { laneChangingQuestions } from './laneChangingQuestions';
import { overtakingQuestions } from './overtakingQuestions';
import { speedLimitsQuestions } from './speedLimitsQuestions';

// Map all question files to their IDs (CBR 2026 consolidated - Phase B complete)
const ALL_QUESTION_FILES = {
  // TIER 1: CRITICAL TOPICS
  roadMarkings: roadMarkingsQuestions,
  motorwayRules: motorwayRulesQuestions,
  trafficSignRecognition: signIdentificationQuestions,
  vehicleHandling: vehicleHandlingQuestions,
  tramInteractions: tramInteractionsQuestions,
  riskAwareness: riskAwarenessQuestions,
  nightVisibility: nightVisibilityQuestions,
  speedLimits: speedLimitsQuestions,

  // TIER 2: IMPORTANT TOPICS
  weatherConditions: weatherConditionsQuestions,
  incidentFirstAid: incidentFirstAidQuestions,
  pedestrianCrossings: pedestrianCrossingsQuestions,
  vehicleCategories: vehicleCategoriesQuestions,
  constructionZones: constructionZonesQuestions,
  laneChanging: laneChangingQuestions,
  overtaking: overtakingQuestions,
  technologySafety: technologySafetyQuestions,
  fatigueRest: fatigueRestQuestions,
  environmentalZones: environmentalZonesQuestions,
  vehicleDocumentation: vehicleDocumentationQuestions,

  // TIER 3: ENHANCEMENT TOPICS
  priorityRules: priorityRulesQuestions,
  mandatorySigns: mandatorySignsQuestions,
  trafficLightsSignals: trafficLightsSignalsQuestions,
  prohibitorySigns: prohibitorySignsQuestions,
  roundaboutRules: roundaboutRulesQuestions,
  emergencyProcedures: emergencyProceduresQuestions,
  alcoholDrugs: alcoholDrugsQuestions,
  specializedDriving: specializedDrivingQuestions,

  // ANIMATION-LINKED TOPICS
  bicycleInteractions: bicycleInteractionsQuestions,
  hazardPerception: riskAwarenessQuestions,

  // BONUS
  adasSafety: adasSafetyQuestions,

  // BACKWARD COMPATIBILITY: Old category structure (for legacy code)
  // INSIGHT CATEGORY
  roadSigns: signIdentificationQuestions,
  weatherVisibility: [...weatherConditionsQuestions, ...nightVisibilityQuestions],

  // HAZARD PERCEPTION CATEGORY (legacy keys — now point to new dedicated files above)

  // KNOWLEDGE CATEGORY
  parkingRules: vehicleDocumentationQuestions,

  // ARCHIVE POOL
  realExamQuestions,
  signIdentification: signIdentificationQuestions
} as const;

export interface QuestionMapping {
  insight: Map<string, Question[]>;
  hazardPerception: Map<string, Question[]>;
  knowledge: Map<string, Question[]>;
  archive: Map<string, Question[]>;
}

export class QuestionMapper {
  private mapping: QuestionMapping;

  constructor() {
    this.mapping = this.createMapping();
  }

  private createMapping(): QuestionMapping {
    const insight = new Map<string, Question[]>();
    const hazardPerception = new Map<string, Question[]>();
    const knowledge = new Map<string, Question[]>();
    const archive = new Map<string, Question[]>();

    // Map insight topics
    INSIGHT_TOPICS.forEach(topicId => {
      const questions = ALL_QUESTION_FILES[topicId as keyof typeof ALL_QUESTION_FILES];
      if (questions && questions.length > 0) {
        insight.set(topicId, [...questions] as Question[]);
      }
    });

    // Map hazard perception topics
    HAZARD_TOPICS.forEach(topicId => {
      const questions = ALL_QUESTION_FILES[topicId as keyof typeof ALL_QUESTION_FILES];
      if (questions && questions.length > 0) {
        hazardPerception.set(topicId, [...questions] as Question[]);
      }
    });

    // Map knowledge topics
    KNOWLEDGE_TOPICS.forEach(topicId => {
      const questions = ALL_QUESTION_FILES[topicId as keyof typeof ALL_QUESTION_FILES];
      if (questions && questions.length > 0) {
        knowledge.set(topicId, [...questions] as Question[]);
      }
    });

    // Map archive topics
    ARCHIVE_TOPICS.forEach(topicId => {
      const questions = ALL_QUESTION_FILES[topicId as keyof typeof ALL_QUESTION_FILES];
      if (questions && questions.length > 0) {
        archive.set(topicId, [...questions] as Question[]);
      }
    });

    return { insight, hazardPerception, knowledge, archive };
  }

  getMapping(): QuestionMapping {
    return this.mapping;
  }

  // Get statistics for each category
  getCategoryStats() {
    const stats = {
      insight: { topics: 0, questions: 0 },
      hazardPerception: { topics: 0, questions: 0 },
      knowledge: { topics: 0, questions: 0 },
      archive: { topics: 0, questions: 0 }
    };

    // Count insight stats
    this.mapping.insight.forEach((questions, _topicId) => {
      stats.insight.topics++;
      stats.insight.questions += questions.length;
    });

    // Count hazard perception stats
    this.mapping.hazardPerception.forEach((questions, _topicId) => {
      stats.hazardPerception.topics++;
      stats.hazardPerception.questions += questions.length;
    });

    // Count knowledge stats
    this.mapping.knowledge.forEach((questions, _topicId) => {
      stats.knowledge.topics++;
      stats.knowledge.questions += questions.length;
    });

    // Count archive stats
    this.mapping.archive.forEach((questions, _topicId) => {
      stats.archive.topics++;
      stats.archive.questions += questions.length;
    });

    return stats;
  }

  // Get specific topic questions
  getTopicQuestions(category: keyof QuestionMapping, topicId: string): Question[] {
    const categoryMap = this.mapping[category];
    if (!categoryMap) return [];
    
    return categoryMap.get(topicId) || [];
  }

  // Get all questions for a category
  getCategoryQuestions(category: keyof QuestionMapping): Question[] {
    const categoryMap = this.mapping[category];
    if (!categoryMap) return [];
    
    return Array.from(categoryMap.values()).flat();
  }

  // Validate mapping completeness
  validateMapping(): { isValid: boolean; issues: string[] } {
    const issues: string[] = [];

    // Check insight category
    INSIGHT_TOPICS.forEach(topicId => {
      if (!this.mapping.insight.has(topicId)) {
        issues.push(`Missing insight topic: ${topicId}`);
      }
    });

    // Check hazard perception category
    HAZARD_TOPICS.forEach(topicId => {
      if (!this.mapping.hazardPerception.has(topicId)) {
        issues.push(`Missing hazard perception topic: ${topicId}`);
      }
    });

    // Check knowledge category
    KNOWLEDGE_TOPICS.forEach(topicId => {
      if (!this.mapping.knowledge.has(topicId)) {
        issues.push(`Missing knowledge topic: ${topicId}`);
      }
    });

    return {
      isValid: issues.length === 0,
      issues
    };
  }
}

// Export singleton instance
export const questionMapper = new QuestionMapper();
